import { groupBy } from 'lodash';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import styled from 'styled-components';
import { MemberType, ProjectId, Team } from 'api/models';
import { EventSource, Project } from 'api/project';
import {
  AssignPlaceholderPayload,
  isBulkPlaceHolderEnabled,
  OPERATION_TYPE,
  TeamsAPI,
  TeamsBulkUpdateStatus,
} from 'api/teams';
import { Loading } from 'components/atoms/loading';
import { Modal as ModalAtom, error } from 'components/atoms/modal';
import { Option } from 'components/atoms/select';
import { Extra } from 'components/molecules/squad-picker';
import { Option as SquadOption } from 'components/molecules/squad-picker/types';
import { useProjectSquad } from 'components/molecules/squad-picker/use-squad-options';
import { teamOption } from 'components/molecules/squad-picker/utils';
import { toastService } from 'components/molecules/toast';
import { translationKeyForAccessLevelLabel } from 'components/organisms/module-settings/partner/utils';
import { useInvalidateOldPlaceholderData } from 'hooks/placeholders/use-placeholder-invalidation';
import { ProjectResource, useWithAccess } from 'hooks/rbac/useWithAccess';
import { useAccount } from 'hooks/use-account';
import {
  useMemberInvitationConfirmation,
  filterNotInProjectUsers,
} from 'hooks/use-invite-user';
import { useTranslate, useTranslation } from 'hooks/use-translation';
import { useUser } from 'hooks/use-user';
import { getProjectMembersQueryKey } from 'queries';
import {
  useMultiAssignPlaceholderMutation,
  usePlaceholdersWithMetaByProjectQuery,
} from 'queries/placeholder';
import {
  useAddProjectUsersLiteMutation,
  useRemoveProjectUsersLiteMutation,
} from 'queries/project';
import { useSetProjectPlaceholderUpdateStatus } from 'queries/projects/use-placeholder-update-progress';
import { sortByText } from 'utils/sort-by-text';
import { DefaultModal } from './modal-types/DefaultModal';
import { ErrorModal } from './modal-types/ErrorModal';
import { InProgress } from './modal-types/InProgress';
import { StatusType } from './placeholder-resolution';
import { usePlaceholderValueHelpers } from './placeholder-value-helper';
import { PlaceholderValueMap } from './types';
import { useModalPropsHelper } from './use-modal-props-helper';

const Modal = styled(ModalAtom)`
  .ant-modal-footer {
    padding: var(--spacing-05);
    border-top: 1px solid var(--border-color);
  }
`;

const LoadingWrapper = styled.div`
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalSelector = (data: string | undefined) => {
  switch (data) {
    case 'IN_PROGRESS': {
      return InProgress;
    }
    case 'FAILED': {
      return ErrorModal;
    }
    default: {
      return DefaultModal;
    }
  }
};

interface ModalProps {
  onClose: () => void;
  visible: boolean;
  placeholders: Team[];
  project: Project;
  teamsUpdateStatusInfo?: TeamsBulkUpdateStatus[] | undefined;
  teamsUpdateStatus?: StatusType;
}
export const PlaceholderResolutionModal: FC<ModalProps> = ({
  onClose,
  visible,
  placeholders,
  project,
  teamsUpdateStatusInfo,
  teamsUpdateStatus,
}) => {
  const {
    getUserAssigned,
    getCompanyAssigned,
    getResponsibleCompanyForOption,
    doesCompanyHaveAcccesForPrivateTasks,
  } = usePlaceholderValueHelpers();

  const userInfo = useUser();

  const getValuesForPlaceholder = useCallback(
    (): PlaceholderValueMap =>
      placeholders.reduce((valueMap, placeholder) => {
        return {
          ...valueMap,
          [placeholder.teamId]:
            getUserAssigned(placeholder)?.option ??
            getCompanyAssigned(placeholder)?.option,
        };
      }, {}),
    [getCompanyAssigned, getUserAssigned, placeholders],
  );
  const [inProgress, setInProgress] = useState(false);
  const [placeholderValueMap, setPlaceholderValueMap] =
    useState<PlaceholderValueMap>(getValuesForPlaceholder());
  const translate = useTranslate();
  const { data: placeholderWithMeta, isFetching: placeholderMetaLoading } =
    usePlaceholdersWithMetaByProjectQuery(ProjectId(project.projectId), {
      refetchOnMount: 'always',
    });

  const changeBulkUpdateStatus = useSetProjectPlaceholderUpdateStatus();

  const placeholderMeta = useMemo(
    () =>
      (placeholderWithMeta ?? []).reduce(
        (metaMap, placeholder) => ({
          ...metaMap,
          [placeholder.teamId]: placeholder.meta,
        }),
        {},
      ),
    [placeholderWithMeta],
  );

  const onPlaceholderValueChange = useCallback(
    (placeholder: Team, selected?: Option<Extra>) => {
      if (
        (placeholderMeta[placeholder.teamId]?.privateTask ?? 0) !== 0 &&
        selected != null
      ) {
        const responsibleCompany = getResponsibleCompanyForOption(selected);

        if (responsibleCompany != null) {
          const access =
            doesCompanyHaveAcccesForPrivateTasks(responsibleCompany);

          if (!access.hasAccess) {
            const title = translate(
              'projects.placeholder_resolutions.cannot_assigned.private_tasks.title',
            );
            const content = translate(
              'projects.placeholder_resolutions.cannot_assigned.private_tasks.description',
              {
                placeholderName: placeholder.teamName,
                companyName: selected.label,
                accessLevel:
                  access.permissionSetName != null
                    ? translate(
                        translationKeyForAccessLevelLabel(
                          access.permissionSetName,
                        ),
                      )
                    : '',
                isUser: selected.extra?.type === 'user',
                numberOfTasks:
                  placeholderMeta[placeholder.teamId]?.privateTask ?? 0,
                strong: (chunks) => <strong>{chunks}</strong>,
              },
            );

            setPlaceholderValueMap((previous) => ({
              ...previous,
              [placeholder.teamId]: null,
            }));

            error({
              title,
              content,
            });

            return;
          }
        }
      }
      setPlaceholderValueMap((previous) => ({
        ...previous,
        [placeholder.teamId]: selected,
      }));
    },
    [
      doesCompanyHaveAcccesForPrivateTasks,
      getResponsibleCompanyForOption,
      placeholderMeta,
      translate,
    ],
  );
  const handleUserSelection = useMemberInvitationConfirmation(
    ProjectId(project.projectId),
  );
  const account = useAccount();
  const queryClient = useQueryClient();
  const invalidateOldPlaceholderData = useInvalidateOldPlaceholderData(project);
  const { mutateAsync: addProjectUsersLite } = useAddProjectUsersLiteMutation();
  const { mutateAsync: removeProjectUserLite } =
    useRemoveProjectUsersLiteMutation();
  const successTitle = useTranslation(
    'projects.placeholder_resolution.success.title',
  );
  const successMessage = useTranslation(
    'projects.placeholder_resolution.success.message',
  );
  const errorTitle = useTranslation(
    'projects.placeholder_resolution.error.title',
  );
  const errorMessage = useTranslation(
    'projects.placeholder_resolution.error.message',
  );
  const { mutateAsync: multiAssignPlaceholder } =
    useMultiAssignPlaceholderMutation(project.projectId, {
      onSuccess: async () => {
        queryClient.invalidateQueries(
          getProjectMembersQueryKey(ProjectId(project.projectId)),
        );
        toastService({
          toastContainer: {},
          title: successTitle,
          message: successMessage,
        }).success();
      },
      onError: () => {
        toastService({
          toastContainer: {},
          title: errorTitle,
          message: errorMessage,
        }).error();
      },
    });

  const {
    data: { notInProject },
  } = useProjectSquad(ProjectId(project.projectId));

  const onSubmit = useCallback(async () => {
    const userOptions = (
      Object.values(placeholderValueMap).filter(Boolean) as Option<Extra>[]
    ).filter((option) => option.extra?.type === 'user');

    const ids = userOptions.map((item) => item?.extra?.id ?? 0);

    const result = filterNotInProjectUsers(ids, notInProject);

    const notInProjectUserIds = result.map(({ userId }) => userId);

    const confirmedUserOptions = await handleUserSelection(
      userOptions as SquadOption[],
    );

    if (confirmedUserOptions.length !== userOptions.length) {
      return;
    }
    const placeholdersWithNewlyAssignedUser = placeholders.filter(
      (placeholder) => {
        const newValueForPlaceholder = placeholderValueMap[placeholder.teamId];

        if (
          newValueForPlaceholder == null ||
          newValueForPlaceholder.extra?.type !== 'user'
        ) {
          return false;
        }

        return (
          newValueForPlaceholder.extra.id !== placeholder.userList?.[0]?.userId
        );
      },
    );

    const placeholdersWithRemovedUser = placeholders.filter((placeholder) => {
      const oldAssignedUserId = placeholder.userList?.[0]?.userId;

      if (oldAssignedUserId == null) {
        return false;
      }

      const isUserAssigned =
        placeholderValueMap[placeholder.teamId]?.extra?.type === 'user';

      return (
        !isUserAssigned ||
        placeholderValueMap[placeholder.teamId]?.extra?.id !== oldAssignedUserId
      );
    });

    setInProgress(true);
    try {
      let removeUserTeams = {};
      let addUserTeams = {};

      if (placeholdersWithRemovedUser.length > 0) {
        removeUserTeams = {
          projectId: project.projectId,
          removeUserTeamList: placeholdersWithRemovedUser
            .map((placeholder) => ({
              userId: placeholder.userList?.[0]?.userId as number,
              roleId: placeholder.roleId,
              referenceTeamId: placeholder.teamId,
            }))
            .filter(({ userId }) => userId != null),
        };

        if (!isBulkPlaceHolderEnabled(account)) {
          // Old Flow
          await removeProjectUserLite({
            projectId: project.projectId,
            projectUsers: placeholdersWithRemovedUser
              .map((placeholder) => ({
                userId: placeholder.userList?.[0]?.userId as number,
                roleId: placeholder.roleId,
                referenceTeamId: placeholder.teamId,
              }))
              .filter(({ userId }) => userId != null),
            source: EventSource.PlaceholderManage,
          });
        }
      }
      if (placeholdersWithNewlyAssignedUser.length > 0) {
        addUserTeams = {
          projectId: project.projectId,
          addUserTeamList: placeholdersWithNewlyAssignedUser.map(
            (placeholder) => ({
              userId: placeholderValueMap[placeholder.teamId]?.extra?.id ?? -1,
              referenceTeamId: placeholder.teamId,
              roleId: placeholder.roleId,
            }),
          ),
          assignedToProjectMember: notInProjectUserIds?.length === 0,
        };

        if (!isBulkPlaceHolderEnabled(account)) {
          // Old Flow
          await addProjectUsersLite(
            {
              projectId: project.projectId,
              projectUsers: placeholdersWithNewlyAssignedUser.map(
                (placeholder) => ({
                  userId:
                    placeholderValueMap[placeholder.teamId]?.extra?.id ?? -1,
                  referenceTeamId: placeholder.teamId,
                  roleId: placeholder.roleId,
                }),
              ),
              source: EventSource.PlaceholderManage,
              assignedToProjectMember: notInProjectUserIds?.length === 0,
            },
            {
              onSuccess: async () => {},
            },
          );
        }
      }
      const payload = placeholders.map((placeholder) => {
        return {
          option: {
            ...teamOption(placeholder),
            user: placeholderValueMap[placeholder.teamId],
          },
          roleId: placeholder.roleId,
        };
      });

      const bulkPayload: AssignPlaceholderPayload[] = [];

      payload.forEach(({ option: _placeholder, roleId }) => {
        bulkPayload.push({
          projectId: project.projectId,
          userList:
            _placeholder?.user?.extra?.type === 'user'
              ? [
                  {
                    userId: Number(_placeholder.user?.extra.id),
                  },
                ]
              : undefined,
          companyList:
            _placeholder?.user?.extra?.type === 'company'
              ? [
                  {
                    companyId: Number(_placeholder.user?.extra.id),
                  },
                ]
              : undefined,
          roleId,
          teamId: _placeholder.extra.id,
        });
      });

      let operationType = OPERATION_TYPE.ADD;

      if (
        Object.keys(addUserTeams).length &&
        Object.keys(removeUserTeams).length
      ) {
        operationType = OPERATION_TYPE.UPDATE;
      } else if (Object.keys(removeUserTeams).length) {
        operationType = OPERATION_TYPE.REMOVE;
      }

      const finalPayload = {
        ...addUserTeams,
        ...removeUserTeams,
        teams: bulkPayload,
        operationType,
        assignedToProjectMember: notInProjectUserIds?.length === 0,
      };

      if (isBulkPlaceHolderEnabled(account)) {
        // New flow
        TeamsAPI.bulkPlaceholdersUpdate(finalPayload, project.projectId);

        changeBulkUpdateStatus([
          { status: 'IN_PROGRESS', createdBy: userInfo.user.userId },
        ]);
      } else {
        // Old flow
        (await multiAssignPlaceholder(
          payload,
        )) as unknown as TeamsBulkUpdateStatus;
      }

      /* FIX: Invalidating queries only for success case. 
         After proper socket implementation for task updates need to invalidate these queries for fail case too. */

      onClose();
      invalidateOldPlaceholderData();
    } catch (e) {
      setInProgress(false);
      toastService({
        toastContainer: {},
        title: errorTitle,
        message: errorMessage,
      }).error();
    }
  }, [
    addProjectUsersLite,
    errorMessage,
    errorTitle,
    handleUserSelection,
    multiAssignPlaceholder,
    onClose,
    placeholderValueMap,
    placeholders,
    project.projectId,
    removeProjectUserLite,
    notInProject,
    changeBulkUpdateStatus,
    account,
    userInfo,
    invalidateOldPlaceholderData,
  ]);

  const sortedPlaceholders = useMemo(() => {
    const grouped = groupBy(placeholders, 'teamType');

    return (grouped[MemberType.Native] ?? [])
      .sort((a, b) => sortByText(a.teamName, b.teamName))
      .concat(
        (grouped[MemberType.External] ?? []).sort((a, b) =>
          sortByText(a.teamName, b.teamName),
        ),
      );
  }, [placeholders]);
  const { hasAccessToResource } = useWithAccess();
  const hasTeamEditAccess = hasAccessToResource({
    rule: 'TASK_ASSIGNEE:WRITE',
    resourceData: ProjectResource(project),
  });

  const isLoading = Boolean(placeholderMetaLoading);

  const [bulkUpdateStatus, setBulkUpdateStatus] = useState(teamsUpdateStatus);

  const Content = ModalSelector(
    isBulkPlaceHolderEnabled(account) ? bulkUpdateStatus : undefined,
  );

  const modalProps = useModalPropsHelper({
    visible,
    inProgress,
    placeholderMetaLoading,
    onClose,
    bulkUpdateStatus,
    onSubmit,
    isLoading,
    setBulkUpdateStatus,
    teamsUpdateStatusInfo,
  });

  return (
    <Modal {...modalProps}>
      {isLoading ? (
        <LoadingWrapper>
          <Loading size="large" />
        </LoadingWrapper>
      ) : (
        <Content
          project={project}
          placeholders={sortedPlaceholders}
          placeholderMeta={placeholderMeta}
          placeholderValueMap={placeholderValueMap}
          onPlaceholderValueChange={onPlaceholderValueChange}
          readOnly={!hasTeamEditAccess}
          updateStatuses={teamsUpdateStatusInfo}
        />
      )}
    </Modal>
  );
};
